import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { WasteStationSignatureResult } from "api/transportSedel.api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cleanupAfterWasteStationSign, UppdragQueryKey } from "api/uppdrag.api";
import { useNotification } from "hooks/useNotification";
import { TransportSedelQueryKey } from "api/transportSedel.api";
import { useCurrentCompany } from "api/company.api";

const createUppdragItemSchema = (isInvoicingEnabled: boolean) => {
  const base = z.object({
    uppdragId: z.number(),
    workedHours: z.string().optional(),
    drivingHours: z.string().optional(),
    isCompleted: z.boolean(),
    isCompletedDisabled: z.boolean(),
  });

  if (isInvoicingEnabled) {
    return base.refine((data) => !!data.workedHours || !!data.drivingHours, {
      message: "Ange arbetad tid eller körtid",
      path: ["workedHours"],
    });
  }

  return base;
};

const createFormSchema = (isInvoicingEnabled: boolean) =>
  z.object({
    uppdragItems: z.array(createUppdragItemSchema(isInvoicingEnabled)),
  });

type FormData = {
  uppdragItems: {
    uppdragId: number;
    workedHours: string;
    drivingHours: string;
    isCompleted: boolean;
    isCompletedDisabled: boolean;
  }[];
};

export const useCompleteUppdragModal = (
  results: WasteStationSignatureResult[],
  isOpen: boolean,
  onClose: () => void,
) => {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { data: company } = useCurrentCompany();
  const isInvoicingEnabled = company?.isInvoicingEnabled || false;

  const form = useForm<FormData>({
    resolver: zodResolver(createFormSchema(isInvoicingEnabled)),
    defaultValues: {
      uppdragItems: [],
    },
  });

  const { fields, replace } = useFieldArray({
    control: form.control,
    name: "uppdragItems",
  });

  const cleanupMutation = useMutation({
    ...cleanupAfterWasteStationSign(),
    onSuccess: () => {
      notification.success("Uppdrag uppdaterade");

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
      queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });

      onClose();
    },
    onError: () => {
      notification.error("Kunde inte uppdatera uppdrag");
    },
  });

  useEffect(() => {
    if (isOpen && results.length > 0) {
      const initialData = results.map((result) => ({
        uppdragId: result.uppdragId,
        workedHours: result.estimatedWorkingHours || "",
        drivingHours: result.estimatedDrivingHours || "",
        isCompleted: result.isReadyToBeCompleted,
        isCompletedDisabled: !result.isReadyToBeCompleted,
      }));
      replace(initialData);
    }
  }, [results, isOpen, replace]);

  const onSubmit = (data: FormData) => {
    // Transform form data to match API DTO structure
    const payload = {
      items: data.uppdragItems
        .filter(
          (item) => item.workedHours || item.drivingHours || item.isCompleted,
        )
        .map((item) => ({
          uppdragId: item.uppdragId,
          reportedWorkingHours: item.workedHours || undefined,
          reportedDrivingHours: item.drivingHours || undefined,
          isCompleted: item.isCompleted || undefined,
        })),
    };

    // Only submit if there are items to update
    if (payload.items.length > 0) {
      cleanupMutation.mutate(payload);
    } else {
      onClose();
    }
  };

  return {
    form,
    fields,
    onSubmit,
    isSubmitting: cleanupMutation.isPending,
    isInvoicingEnabled,
  };
};
